/**
 * Lilja package picker — radio packages + multi-item /cart/add.js.
 * PDP totals: catalog prices minus kr discounts from section settings.
 * Real cart prices come from Shopify Admin Buy X get Y discounts.
 */
(function () {
  const EVENT_SELECTION = 'lilja:bundle-selection';

  function parseConfig(root) {
    const el = root.querySelector('[data-lilja-bundle-config]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.error('Lilja bundle: invalid config', e);
      return null;
    }
  }

  function catalogCents(item) {
    const price = Number(item && item.price) || 0;
    const listed = Number(item && item.listPrice);
    if (listed > 0) return listed;
    const compare = Number(item && item.compareAtPrice) || 0;
    return compare > price ? compare : price;
  }

  function formatSavingsValue(listCents, payCents, format) {
    const shownSave = Math.max(0, (Number(listCents) || 0) - (Number(payCents) || 0));
    if (shownSave <= 0) return '';
    const amount = formatSavingsMoney(shownSave);
    const list = Number(listCents) || 0;
    const percent = list > 0 ? Math.round((shownSave / list) * 100) : 0;
    if (format === 'amount') return amount;
    if (format === 'both') return `${percent}% (${amount})`;
    return `${percent}%`;
  }

  function formatSavingsMoney(cents) {
    const amount = (Number(cents) || 0) / 100;
    if (Math.abs(amount - Math.round(amount)) < 0.0005) {
      return `${Math.round(amount)} kr`;
    }
    return `${amount.toFixed(2).replace('.', ',')} kr`;
  }

  function setError(root, message) {
    const err = root.querySelector('[data-lilja-bundle-error]');
    if (!err) return;
    if (message) {
      err.hidden = false;
      err.textContent = message;
    } else {
      err.hidden = true;
      err.textContent = '';
    }
  }

  class LiljaBundleWidget {
    constructor(root, config) {
      this.root = root;
      this.config = config;
      this.addons = (config.addons || []).map((a) => ({
        ...a,
        quantity: Number(a.quantity) || 0,
      }));
      this.packages = config.packages || [];
      this.cart =
        document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.addButton = root.querySelector('[data-lilja-bundle-add]');
      this.nativeForm = null;
      this.nativeSubmit = null;
      this.error = false;

      root.liljaBundleWidget = this;

      this.bindPackageRadios();
      this.bindNativeForm();
      if (this.addButton) {
        this.addButton.addEventListener('click', () => this.onAdd());
      }

      this.syncAddonQuantities();
      this.render();
    }

    selectedPackage() {
      const radio = this.root.querySelector('[data-lilja-package]:checked');
      const id = radio ? radio.value : 'solo';
      return (
        this.packages.find((pkg) => pkg.id === id) || {
          id: 'solo',
          title: '',
          addonBlockIds: [],
          saveCents: 0,
          baseSaveCents: 0,
          addonSaves: {},
        }
      );
    }

    packageTitle() {
      const pkg = this.selectedPackage();
      if (pkg.title) return pkg.title;
      const radio = this.root.querySelector('[data-lilja-package]:checked');
      const option = radio && radio.closest('.lilja-bundle__option');
      const titleEl = option && option.querySelector('.lilja-bundle__option-title');
      const fromDom = titleEl ? titleEl.textContent.trim() : '';
      return fromDom || this.config.base.title || '';
    }

    itemDisplayTitle(addon) {
      const title = (addon && addon.title) || '';
      const pack = addon && addon.packLabel ? String(addon.packLabel).trim() : '';
      return pack ? `${title} ${pack}`.trim() : title;
    }

    selectedItems() {
      const pkg = this.selectedPackage();
      const items = [];
      const base = this.config.base || {};
      const baseSelling = Number(base.price) || 0;
      const baseList = catalogCents(base);
      const baseSave = Math.min(Math.max(0, Number(pkg.baseSaveCents) || 0), baseSelling);
      const basePay = baseSelling - baseSave;
      items.push({
        title: base.title || '',
        image: base.image || '',
        payCents: basePay,
        listCents: baseList,
        payDisplay: formatSavingsMoney(basePay),
        listDisplay: baseList > basePay ? formatSavingsMoney(baseList) : '',
      });

      const ids = (pkg.addonBlockIds || []).map((id) => String(id));
      const saves = pkg.addonSaves || {};
      this.addons.forEach((addon) => {
        const blockId = String(addon.blockId);
        if (ids.indexOf(blockId) < 0) return;
        const selling = Number(addon.price) || 0;
        const list = catalogCents(addon);
        const save = Math.min(Math.max(0, Number(saves[blockId]) || Number(saves[addon.blockId]) || 0), selling);
        const pay = selling - save;
        items.push({
          title: this.itemDisplayTitle(addon),
          image: addon.image || '',
          payCents: pay,
          listCents: list,
          payDisplay: formatSavingsMoney(pay),
          listDisplay: list > pay ? formatSavingsMoney(list) : '',
        });
      });
      return items;
    }

    syncAddonQuantities() {
      const ids = this.selectedPackage().addonBlockIds || [];
      this.addons.forEach((addon) => {
        addon.quantity = ids.indexOf(addon.blockId) >= 0 ? 1 : 0;
      });
    }

    bindPackageRadios() {
      this.root.querySelectorAll('[data-lilja-package]').forEach((input) => {
        input.addEventListener('change', () => {
          this.syncAddonQuantities();
          this.render();
        });
      });
    }

    bindNativeForm() {
      const scope = this.root.closest('product-info') || document;
      const form =
        scope.querySelector('.product__info-container form[data-type="add-to-cart-form"]') ||
        scope.querySelector('form[data-type="add-to-cart-form"]');
      if (!form) return;

      this.nativeForm = form;
      this.nativeSubmit = form.querySelector('[type="submit"]');

      form.addEventListener(
        'submit',
        (evt) => {
          if (this.addonUnits() < 1) return;
          evt.preventDefault();
          evt.stopImmediatePropagation();
          this.onAdd({ submitButton: this.nativeSubmit });
        },
        true
      );
    }

    addonUnits() {
      return this.addons.reduce((sum, a) => sum + (Number(a.quantity) || 0), 0);
    }

    totalUnits() {
      return 1 + this.addonUnits();
    }

    estimate() {
      this.syncAddonQuantities();
      const pkg = this.selectedPackage();
      const baseSelling = Number(this.config.base.price) || 0;
      const baseList = catalogCents(this.config.base);
      let addonSelling = 0;
      let addonList = 0;
      this.addons.forEach((a) => {
        if ((Number(a.quantity) || 0) < 1) return;
        addonSelling += Number(a.price) || 0;
        addonList += catalogCents(a);
      });
      const sellingCents = baseSelling + addonSelling;
      const listCents = baseList + addonList;
      const saveCents = Math.min(Math.max(0, Number(pkg.saveCents) || 0), sellingCents);
      return {
        listCents,
        payableCents: sellingCents - saveCents,
        saveCents,
        units: this.addonUnits(),
      };
    }

    buildPayloadItems() {
      const items = [];
      const base = this.config.base;
      if (!base || !base.id) return items;

      let baseId = Number(base.id);
      let baseQty = 1;
      if (this.nativeForm) {
        const idInput = this.nativeForm.querySelector('[name="id"]');
        const qtyInput = this.nativeForm.querySelector('[name="quantity"]');
        if (idInput && idInput.value) baseId = Number(idInput.value);
        if (qtyInput && qtyInput.value) {
          const parsedQty = Number(qtyInput.value);
          if (parsedQty > 0) baseQty = parsedQty;
        }
      }

      const hasAddons = this.addonUnits() > 0;
      const key = (this.config.bundleKey || '').trim();

      const baseRow = {
        id: baseId,
        quantity: baseQty,
      };
      if (hasAddons && key) {
        baseRow.properties = { _bundle: key };
      }
      items.push(baseRow);

      this.addons.forEach((a) => {
        const qty = Number(a.quantity) || 0;
        if (qty < 1 || !a.id) return;
        const row = {
          id: Number(a.id),
          quantity: qty,
        };
        if (key) {
          row.properties = { _bundle: key };
        }
        items.push(row);
      });

      return items;
    }

    emitSelection() {
      const est = this.estimate();
      const title = this.packageTitle();
      document.dispatchEvent(
        new CustomEvent(EVENT_SELECTION, {
          detail: {
            label: title,
            packageTitle: title,
            priceDisplay: formatSavingsMoney(est.payableCents),
            listPriceDisplay: est.listCents > est.payableCents ? formatSavingsMoney(est.listCents) : '',
            savingsText: (function () {
              const value = formatSavingsValue(
                est.listCents,
                est.payableCents,
                this.config.savingsFormat
              );
              if (!value) return '';
              const template =
                (this.config.i18n && this.config.i18n.savings) || 'Du sparer __AMOUNT__';
              return String(template).replace(/__AMOUNT__/g, value);
            }).call(this),
            bundleKey: est.units > 0 ? this.config.bundleKey || '' : '',
            units: this.totalUnits(),
            addonUnits: est.units,
            packageId: this.selectedPackage().id,
            items: this.selectedItems(),
          },
        })
      );
    }

    render() {
      const selected = this.selectedPackage().id;
      this.root.querySelectorAll('.lilja-bundle__option').forEach((option) => {
        const input = option.querySelector('[data-lilja-package]');
        const on = input && input.value === selected && input.checked;
        option.classList.toggle('is-active', !!on);
      });
      this.renderContents();
      this.emitSelection();
    }

    renderContents() {
      this.root.querySelectorAll('[data-lilja-bundle-contents]').forEach((panel) => {
        const option = panel.closest('.lilja-bundle__option');
        const input = option && option.querySelector('[data-lilja-package]');
        const on = !!(input && input.checked);
        panel.setAttribute('aria-hidden', on ? 'false' : 'true');
        if ('inert' in panel) panel.inert = !on;
      });
    }

    onAdd(opts) {
      this.syncAddonQuantities();
      const options = opts || {};
      const base = this.config.base;
      const i18n = this.config.i18n || {};
      const button = options.submitButton || this.addButton || this.nativeSubmit;

      if (!base || !base.available) {
        setError(this.root, i18n.soldOut || 'Udsolgt');
        return;
      }

      const unavailable = this.addons.find((a) => a.quantity > 0 && a.available === false);
      if (unavailable) {
        setError(this.root, i18n.soldOut || 'Udsolgt');
        return;
      }

      const items = this.buildPayloadItems();
      if (!items.length) {
        setError(this.root, i18n.errorGeneric || 'Kunne ikke tilføje til kurven.');
        return;
      }

      setError(this.root, '');
      const spinner = button ? button.querySelector('.loading__spinner') : null;
      if (button) {
        button.setAttribute('aria-disabled', 'true');
        button.classList.add('loading');
      }
      if (spinner) spinner.classList.remove('hidden');

      const units = items.reduce((sum, row) => sum + (Number(row.quantity) || 0), 0);
      const feedback =
        window.LiljaCartFeedback && typeof window.LiljaCartFeedback.optimisticAdd === 'function'
          ? window.LiljaCartFeedback.optimisticAdd(units)
          : null;

      const body = { items };

      if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
        body.sections = this.cart
          .getSectionsToRender()
          .map((section) => section.id)
          .join(',');
        body.sections_url = window.location.pathname;
        if (typeof this.cart.setActiveElement === 'function') {
          this.cart.setActiveElement(document.activeElement);
        }
      }

      const rootUrl =
        (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const addUrl =
        (window.routes && window.routes.cart_add_url) || `${rootUrl}cart/add.js`;

      fetch(addUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(body),
      })
        .then((res) => res.json().then((data) => ({ ok: res.ok, status: res.status, data })))
        .then(({ ok, status, data }) => {
          if (!ok || data.status || data.message === 'Cart Error') {
            if (feedback) feedback.revert();
            const msg =
              data.description ||
              data.message ||
              (status === 422
                ? 'En vare er udsolgt.'
                : i18n.errorGeneric || 'Kunne ikke tilføje til kurven.');
            setError(this.root, msg);
            this.error = true;
            return;
          }

          this.error = false;

          if (typeof liljaCapture === 'function') {
            liljaCapture('product_added', {
              product_title: base.title,
              quantity: units,
              addon_units: this.addonUnits(),
              bundle_key: this.addonUnits() > 0 ? this.config.bundleKey || null : null,
              package_id: this.selectedPackage().id,
              currency: window.Shopify?.currency?.active,
            });
          }

          if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'lilja-bundle',
              cartData: data,
            });
          }

          return this.refreshCartDrawer(data);
        })
        .catch((e) => {
          console.error(e);
          if (feedback) feedback.revert();
          setError(this.root, i18n.errorGeneric || 'Noget gik galt. Prøv igen.');
          this.error = true;
        })
        .finally(() => {
          if (button) {
            button.classList.remove('loading');
            if (!this.error) button.removeAttribute('aria-disabled');
          }
          if (spinner) spinner.classList.add('hidden');
        });
    }

    refreshCartDrawer(data) {
      if (!this.cart) {
        if (window.routes && window.routes.cart_url) window.location = window.routes.cart_url;
        return Promise.resolve();
      }

      this.cart.classList.remove('is-empty');

      const needsSections =
        !data.sections ||
        !data.sections['cart-drawer'] ||
        !data.sections['cart-icon-bubble'];

      const paint = (payload) => {
        if (typeof this.cart.renderContents === 'function') {
          this.cart.renderContents(payload);
        }
      };

      if (!needsSections) {
        paint(data);
        return Promise.resolve();
      }

      const sectionIds = this.cart
        .getSectionsToRender()
        .map((section) => section.id)
        .join(',');
      const rootUrl =
        (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const sectionsUrl = `${rootUrl}?sections=${encodeURIComponent(sectionIds)}`;

      return fetch(sectionsUrl)
        .then((res) => res.json())
        .then((sections) => {
          paint({ ...data, sections });
        })
        .catch((e) => {
          console.error('Lilja bundle: section refresh failed', e);
          if (window.routes && window.routes.cart_url) window.location = window.routes.cart_url;
        });
    }
  }

  function init() {
    document.querySelectorAll('[data-lilja-bundle]').forEach((root) => {
      if (root.dataset.liljaBundleReady) return;
      const config = parseConfig(root);
      if (!config || !config.base || !config.base.id) return;
      root.dataset.liljaBundleReady = 'true';
      new LiljaBundleWidget(root, config);
    });
  }

  function refresh() {
    document.querySelectorAll('[data-lilja-bundle]').forEach((root) => {
      const widget = root.liljaBundleWidget;
      if (!widget) return;
      widget.syncAddonQuantities();
      widget.render();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);

  window.addEventListener('pageshow', refresh);

  window.LiljaBundleEvents = { SELECTION: EVENT_SELECTION };
})();
